import { useCallback } from 'react';
import {
  saveReportIndicators,
  SaveReportIndicatorsVariables,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/api';
import { useClient } from '../../apollo';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import { IndicatorSectionKey } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { IndicatorDetails } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { useIndicator } from './';

export type SaveReportIndicatorsResponse = {
  newNpoIndicator: {
    success: boolean;
    id?: number;
  };
};

/**
 * Patch shape for indicator detail saves. Contains every field the
 * `newNpoIndicator` mutation accepts except the identifying keys
 * (`reportId`, `indicatorId`).
 */
type IndicatorDetailsPatch = Omit<
  SaveReportIndicatorsVariables,
  'reportId' | 'indicatorId'
>;

const stripUndefined = <T extends object>(obj: T): Partial<T> => {
  const result: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach(key => {
    if (obj[key] !== undefined) {
      result[key] = obj[key];
    }
  });
  return result;
};

/**
 * Low-level hook: fires the `newNpoIndicator` mutation and exposes a
 * per-call request state. Does NOT touch any provider state. Use this
 * when the caller manages its own indicator state (e.g. ReportBuilder
 * summary screens) and just needs a tracked API call.
 */
export const useSaveReportIndicators = () => {
  const client = useClient();
  const [requestState, makeRequest, setRequestState] = useGraphQLRequest<
    SaveReportIndicatorsResponse
  >(undefined, true);

  const save = useCallback(
    (variables: SaveReportIndicatorsVariables) => {
      const request = saveReportIndicators(client!, variables);
      makeRequest(request);
      return request;
    },
    [client, makeRequest],
  );

  return [save, requestState, setRequestState] as const;
};

/**
 * High-level hook for components that read `impactDetails` from the
 * IndicatorProvider. Performs a single multi-field save of an indicator's
 * detail properties and merges the patch into `impactDetails` on success.
 *
 * Returns a per-call request state so multiple consumers can track their
 * own loading/error UI without sharing a global flag.
 */
export const useSaveDetailProperties = () => {
  const { reportId, setImpactDetails } = useIndicator();
  const [save, requestState] = useSaveReportIndicators();

  const saveDetailProperties = useCallback(
    async (
      indicatorId: number,
      sectionKey: IndicatorSectionKey,
      patch: IndicatorDetailsPatch,
    ): Promise<boolean> => {
      if (!reportId) return false;
      const cleaned = stripUndefined(patch);
      if (Object.keys(cleaned).length === 0) return true;

      const response = await save({
        reportId,
        indicatorId,
        ...cleaned,
      });
      if (!response.data?.newNpoIndicator?.success) return false;

      setImpactDetails(s => {
        const newState = { ...s };
        const index = newState[sectionKey].findIndex(
          i => i.indicatorId === indicatorId,
        );
        if (index < 0) return s;
        newState[sectionKey] = [...newState[sectionKey]];
        newState[sectionKey][index] = {
          ...newState[sectionKey][index],
          ...(cleaned as Partial<IndicatorDetails>),
        };
        return newState;
      });
      return true;
    },
    [reportId, save, setImpactDetails],
  );

  return [saveDetailProperties, requestState] as const;
};
